import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';

const DuplicateCompanyPanel = ({ groups, onOpenCompany, onMergeGroup }) => {
  const [activeGroup, setActiveGroup] = useState(null);
  const [primaryId, setPrimaryId] = useState('');

  if (!groups.length) return null;

  const openMerge = (group) => {
    setActiveGroup(group);
    setPrimaryId(group.companies[0]?.company_id || '');
  };

  const closeMerge = () => {
    setActiveGroup(null);
    setPrimaryId('');
  };

  const confirmMerge = async () => {
    if (!activeGroup || !primaryId) return;

    const duplicate = activeGroup.companies.find(
      company => company.company_id !== primaryId
    );

    if (!duplicate) return;

    await onMergeGroup({
      group: activeGroup,
      primaryId,
      duplicateId: duplicate.company_id
    });

    closeMerge();
  };

  return (
    <section className="rounded-[2rem] border border-amber-200 bg-amber-50/70 p-6 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100">
            <SafeIcon icon="⚠️" className="text-xl" />
          </div>
          <div>
            <h2 className="text-lg font-black text-amber-950">Mulige duplikater</h2>
            <p className="mt-1 text-sm font-medium text-amber-800">
              {groups.length} selskap{groups.length === 1 ? '' : 'er'} kan være registrert flere ganger.
            </p>
          </div>
        </div>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-black uppercase tracking-widest text-amber-700 shadow-sm">
          Kontroll nødvendig
        </span>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-2">
        {groups.slice(0, 6).map((group) => (
          <div key={group.key} className="rounded-2xl border border-amber-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-600">
                {group.reason}
              </span>
              <span className="text-xs font-bold text-slate-400">{group.score}% treff</span>
            </div>

            <div className="space-y-2">
              {group.companies.map((company) => (
                <button
                  key={company.company_id}
                  type="button"
                  onClick={() => onOpenCompany(company)}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-left transition hover:border-primary-200 hover:bg-primary-50"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black text-slate-800">
                      {company.name || 'Uten navn'}
                    </span>
                    <span className="block truncate text-xs font-medium text-slate-500">
                      {company.org_number || company.city || 'Ingen organisasjonsinformasjon'}
                    </span>
                  </span>
                  <SafeIcon icon="➡️" className="shrink-0 text-sm" />
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => openMerge(group)}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition hover:bg-amber-700"
            >
              <SafeIcon icon="🔗" className="text-sm" />
              Slå sammen trygt
            </button>
          </div>
        ))}
      </div>

      {groups.length > 6 && (
        <p className="mt-4 text-center text-xs font-bold text-amber-700">
          Viser 6 av {groups.length} mulige duplikatgrupper.
        </p>
      )}

      {activeGroup && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-black text-slate-900">Velg hovedselskap</h3>
              <p className="mt-1 text-sm font-medium text-slate-500">
                Kontakter og deals flyttes til selskapet du velger. Ingen data slettes.
              </p>
            </div>
            <button
              type="button"
              onClick={closeMerge}
              className="rounded-lg px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Lukk"
            >
              ✕
            </button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {activeGroup.companies.map(company => (
              <label
                key={company.company_id}
                className={`cursor-pointer rounded-xl border p-4 transition ${
                  primaryId === company.company_id
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-slate-200 hover:border-primary-200'
                }`}
              >
                <input
                  type="radio"
                  name="primary-company"
                  value={company.company_id}
                  checked={primaryId === company.company_id}
                  onChange={(event) => setPrimaryId(event.target.value)}
                  className="sr-only"
                />
                <span className="block text-sm font-black text-slate-900">
                  {company.name || 'Uten navn'}
                </span>
                <span className="mt-1 block text-xs font-medium text-slate-500">
                  {company.org_number || company.city || 'Ingen organisasjonsinformasjon'}
                </span>
                <span className="mt-3 inline-block text-[10px] font-black uppercase tracking-widest text-primary-700">
                  {primaryId === company.company_id ? 'Behold dette' : 'Marker som duplikat'}
                </span>
              </label>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={closeMerge}
              className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={confirmMerge}
              className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:bg-slate-800"
            >
              Bekreft sammenslåing
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default DuplicateCompanyPanel;